import type { IObservableArray } from 'mobx';
import { runInAction, when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';

import type {
  Account,
  Billable,
  DisplayCreative,
  Event,
  Goal,
  Partner,
  Segment,
  Targetable,
  Targeting,
  TTDCampaign,
  User,
} from '@feathr/blackbox';
import { CampaignState, GEO_TARGETING_KINDS } from '@feathr/blackbox';
import {
  ActionBar,
  Button,
  ButtonValid,
  generateStepHash,
  parseStepFromHash,
  Spinner,
  usePoof,
  WizardV2,
} from '@feathr/components';
import AddCreativeButton from '@feathr/extender/components/Creatives/AddCreativeButton';
import {
  useBlackboxVersion,
  useCampaignText,
  useClearUnsavedChanges,
} from '@feathr/extender/hooks';
import { useAccount, useFlags, useLocalUrl, useStore, useUser } from '@feathr/extender/state';
import { flattenErrors, getIconForAction } from '@feathr/hooks';
import type { ListResponse } from '@feathr/rachis';

import createCampaignStore, { CampaignContext } from '../CampaignEditPage.context';
import { useChannelAvailability } from '../CampaignEditPage.useChannelAvailability';
import SaveCampaignButton, { save } from '../SaveCampaignButton';
import StepGoals, { getGoalSegments, validateStepGoals } from '../StepGoals';
import { validateStepValue } from '../StepValue';
import WizardGeneralStep, {
  type IValidateStepGeneralConfig,
  validateStepGeneral,
} from '../WizardGeneralStep';
import { useCampaignValidation } from '.';
import type { IUniqueEmailsResult } from './AddWizard.useReconcileUniqueEmails';
import AdWizardAudienceStep, {
  getEphemeralTargetables,
  getTargetSegments,
  validateStepTargets,
} from './AdWizardAudienceStep';
import AdWizardBudgetStep, { validateStepBudget } from './AdWizardBudgetStep';
import AdWizardCreativesStep, { validateStepCreatives } from './AdWizardCreativesStep';
import AdWizardCreativesStepV2 from './AdWizardCreativesStepV2';
import AdWizardFiltersStep, { validateStepFilters } from './AdWizardFiltersStep';
import AdWizardOptimizationStep, { validateStepOptimize } from './AdWizardOptimizationStep';
import AdWizardReviewStep, { validatePartner } from './AdWizardReviewStep';

import * as styles from './AdWizard.css';

interface IProps {
  campaign: TTDCampaign;
}

export interface ICampaignDetails {
  campaign: TTDCampaign;
  targetings: IObservableArray<Targeting>;
  targetingSegments?: Segment[];
  targetables?: Targetable[];
  goals: IObservableArray<Goal>;
  goalSegments?: Segment[];
  creatives: IObservableArray<DisplayCreative>;
  billable?: Billable;
  account: Account;
  event: Event;
  monetizationValue?: number;
  partner?: Partner;
  emailValidation: IUniqueEmailsResult;
}

const getCompletedStep = (
  {
    account,
    campaign,
    creatives,
    goals,
    goalSegments,
    targetings,
    targetingSegments,
    targetables,
    emailValidation,
  }: ICampaignDetails,
  hideFilters: boolean,
  isOnProduction: boolean,
  user?: User,
  noMaxBudget?: boolean,
  generalConfig?: IValidateStepGeneralConfig,
): number => {
  // When hideFilters is true, step indices after Audience are shifted down by 1
  const offset = hideFilters ? 1 : 0;

  if (validateStepGeneral(campaign, generalConfig).length) {
    return 0;
  }
  if (
    targetings.length === 0 ||
    flattenErrors(
      validateStepTargets({
        campaign,
        targetings,
        segments: targetingSegments ?? [],
        targetables: targetables ?? [],
        emailValidation,
      }),
    ).length
  ) {
    return 1;
  }
  // For OmniReach campaigns, don't advance past audience step if only geo targetings exist
  if (campaign.isOmniReachCampaign) {
    const hasOnlyGeoTargetings = targetings
      .filter((t) => !t.get('is_archived'))
      .every((t) => GEO_TARGETING_KINDS.includes(t.get('kind')));
    if (hasOnlyGeoTargetings) {
      return 1;
    }
  }
  if (!hideFilters && validateStepFilters(campaign, targetings).length) {
    return 2;
  }
  if (validateStepCreatives(campaign, creatives).length) {
    return 3 - offset;
  }
  if (flattenErrors(validateStepOptimize(campaign)).length) {
    return 4 - offset;
  }
  if (
    flattenErrors(validateStepBudget({ campaign, account, user, noMaxBudget, isOnProduction }))
      .length
  ) {
    return 5 - offset;
  }
  if (campaign.isMonetization && flattenErrors(validateStepValue(campaign)).length) {
    return 6 - offset;
  } else if (
    flattenErrors(
      validateStepGoals({
        goals,
        goalSegments,
        campaignObjective: campaign.get('objective'),
      }),
    ).length
  ) {
    return 6 - offset;
  }
  return 7 - offset;
};

// TODO: incorporate new wizard hooks here #4560
const getCurrentStepErrors = (
  {
    account,
    campaign,
    creatives,
    goals,
    goalSegments,
    targetings,
    targetingSegments,
    targetables,
    partner,
    emailValidation,
  }: ICampaignDetails,
  hideFilters: boolean,
  isOnProduction: boolean,
  user?: User,
  noMaxBudget?: boolean,
  currentStep?: number,
  generalConfig?: IValidateStepGeneralConfig,
): string[] => {
  const generalErrors = validateStepGeneral(campaign, generalConfig);
  const audienceErrors = flattenErrors(
    validateStepTargets({
      campaign,
      targetings,
      segments: targetingSegments ?? [],
      targetables: targetables ?? [],
      emailValidation,
    }),
  );
  const filtersErrors = validateStepFilters(campaign, targetings);
  const creativesErrors = validateStepCreatives(campaign, creatives);
  const optimizationErrors = flattenErrors(validateStepOptimize(campaign));
  const budgetErrors = flattenErrors(
    validateStepBudget({ campaign, account, user, noMaxBudget, isOnProduction }),
  );
  const goalsErrors = campaign.isMonetization
    ? flattenErrors(validateStepValue(campaign))
    : flattenErrors(
        validateStepGoals({
          goals,
          goalSegments,
          campaignObjective: campaign.get('objective'),
        }),
      );
  const reviewErrors = partner ? validatePartner(partner) : [];

  // Build step-to-errors map based on whether filters step is shown
  const stepErrors: string[][] = hideFilters
    ? [
        generalErrors,
        audienceErrors,
        creativesErrors,
        optimizationErrors,
        budgetErrors,
        goalsErrors,
        reviewErrors,
      ]
    : [
        generalErrors,
        audienceErrors,
        filtersErrors,
        creativesErrors,
        optimizationErrors,
        budgetErrors,
        goalsErrors,
        reviewErrors,
      ];

  if (typeof currentStep === 'number') {
    return stepErrors[currentStep] ?? [];
  }

  return stepErrors.flat();
};

function AdWizard({ campaign }: IProps): JSX.Element {
  const { Creatives, Billables, Events, Goals, Segments, Targetings, Targetables, Partners } =
    useStore();
  const account = useAccount();
  const location = useLocation();
  const { noMaxBudget, metaV2 } = useFlags();
  const user = useUser();
  const { t } = useTranslation();
  const history = useHistory();
  const localUrl = useLocalUrl();
  const { disableStopOrRepublishPastEndDate } = useCampaignText();
  const { childModels, grandchildModels, getValidationErrors, emailValidation } =
    useCampaignValidation({
      campaign,
    });
  useClearUnsavedChanges({
    model: campaign,
    onCleanup: () => {
      Goals.clearApiCache();
    },
  });
  const { isOnProduction } = useBlackboxVersion();
  const poof = usePoof();

  const {
    isDraft,
    isGeofencingCampaign,
    isOmniCampaign,
    isOmniReachCampaign,
    isStopped,
    isPublishing,
    isPastEndDate,
  } = campaign;
  const hideFilters = isGeofencingCampaign || isOmniReachCampaign;
  const showV2Creatives = !!metaV2 && isOmniCampaign;
  const creativesStepIndex = hideFilters ? 2 : 3;
  const generalConfig: IValidateStepGeneralConfig = {
    hasMetaV2: !!metaV2,
    isOmniCampaign,
  };

  const defaultStep = parseStepFromHash(location.hash);

  const [currentStep, setCurrentStep] = useState(defaultStep ?? 0);

  // Update both state and URL hash when step changes
  const updateCurrentStep = (step: number): void => {
    setCurrentStep(step);
    // Use window.location.hash directly to avoid triggering navigation prompts
    window.location.hash = generateStepHash(step);
  };
  const [completedStep, setCompletedStep] = useState<number | undefined>(defaultStep);
  const [didCompleteStep, setDidCompleteStep] = useState<boolean>(false);

  const partnerId = campaign.isMonetization ? campaign.get('parent') : undefined;
  const partner = partnerId ? Partners.get(partnerId) : undefined;

  const event = Events.get(campaign.get('event'));
  const billing = event.get('billing');
  const billable =
    !event.isPending && billing && billing.billable_id
      ? Billables.get(billing.billable_id)
      : undefined;

  const targetings = Targetings.list({
    filters: {
      _parent: campaign.id,
      is_archived__ne: true,
    },
    pagination: { page_size: 1000 },
  });
  const goals = Goals.list({
    filters: {
      _parent: campaign.id,
      is_archived__ne: true,
    },
  });
  // Campaigns only have DisplayCreatives.
  const creatives = Creatives.list({
    filters: {
      _parent: campaign.id,
      is_archived__ne: true,
    },
    ordering: ['-date_created'],
  }) as ListResponse<DisplayCreative>;

  const { availableChannels, hasDisplayCreatives, hasMetaCreatives } = useChannelAvailability({
    campaign,
    creatives: creatives.models,
  });

  const doCompleteStep = (): void => {
    if (
      !event.isPending &&
      !targetings.isPending &&
      !goals.isPending &&
      !creatives.isPending &&
      !emailValidation.isLoading &&
      !didCompleteStep
    ) {
      const goalSegments = getGoalSegments(goals.models, Segments);
      const targetingSegments = getTargetSegments(targetings.models, Segments);
      const targetables = getEphemeralTargetables(targetings.models, Targetables, campaign);
      Promise.all([
        ...goalSegments.map((s) => when(() => !s.isPending)),
        ...targetingSegments.map((s) => when(() => !s.isPending)),
        ...targetables.map((t) => when(() => !t.isPending)),
      ]).then(() => {
        runInAction(() => {
          const step = getCompletedStep(
            {
              campaign,
              targetings: targetings.models,
              targetingSegments,
              targetables,
              goals: goals.models,
              goalSegments,
              creatives: creatives.models,
              billable,
              account,
              event,
              partner,
              emailValidation,
            },
            hideFilters,
            isOnProduction,
            user,
            noMaxBudget,
            generalConfig,
          );
          updateCurrentStep(defaultStep ?? step);
          setCompletedStep(step);
          setDidCompleteStep(true);
        });
      });
    }
  };

  useEffect(doCompleteStep, [
    account,
    billable,
    billable?.isPending,
    campaign,
    creatives.isPending,
    creatives.models,
    defaultStep,
    didCompleteStep,
    emailValidation.isLoading,
    event,
    event.isPending,
    noMaxBudget,
    goals.isPending,
    goals.models,
    hideFilters,
    Segments,
    Targetables,
    targetings.isPending,
    targetings.models,
    user,
    partner,
  ]);

  function onNext(): void {
    const nextStep = currentStep + 1;
    updateCurrentStep(nextStep);
    if (nextStep > (completedStep ?? 0)) {
      setCompletedStep(nextStep);
    }
  }

  function onPrev(): void {
    updateCurrentStep(currentStep - 1);
  }

  const isLoading =
    campaign.isPending ||
    goals.isPending ||
    event.isPending ||
    targetings.isPending ||
    creatives.isPending ||
    emailValidation.isLoading ||
    currentStep === -1 ||
    completedStep === undefined;

  if (isLoading) {
    return <Spinner className={styles.spinner} />;
  }

  const lastStep = hideFilters ? 6 : 7;

  function getErrors(step?: number): string[] {
    return getCurrentStepErrors(
      {
        campaign,
        targetings: targetings.models,
        targetingSegments: getTargetSegments(targetings.models, Segments),
        targetables: getEphemeralTargetables(targetings.models, Targetables, campaign),
        goals: goals.models,
        goalSegments: getGoalSegments(goals.models, Segments),
        creatives: creatives.models,
        billable,
        account,
        event,
        partner,
        emailValidation,
      },
      hideFilters,
      isOnProduction,
      user,
      noMaxBudget,
      step,
      generalConfig,
    );
  }

  const campaignStore = createCampaignStore({ targetings });
  const stepSpecificErrors = getErrors(currentStep);
  const allErrors = getErrors();

  // Returns error status and errors for steps that have been visited and have validation errors
  const getStepValidation = (
    stepIndex: number,
  ): { status: 'error' | undefined; errors: string[] | undefined } => {
    // Only show error for steps the user has visited (at or before completed step)
    if (stepIndex > (completedStep ?? 0)) {
      return { status: undefined, errors: undefined };
    }
    const errors = getErrors(stepIndex);
    const hasErrors = errors.length > 0;
    return {
      status: hasErrors ? 'error' : undefined,
      errors: hasErrors ? errors : undefined,
    };
  };

  async function handlePublish(): Promise<void> {
    await save({
      campaign,
      childModels,
      grandchildModels,
      shouldChangeState: true,
      t,
      accountId: account.id,
    });
    history.push(localUrl(campaign.getItemUrl()));
  }

  return (
    <CampaignContext.Provider value={campaignStore}>
      <ActionBar
        left={
          <>
            <Button
              disabled={currentStep <= 0 || isLoading}
              onClick={onPrev}
              prefix={getIconForAction('previous')}
            >
              {t('Previous')}
            </Button>
            <ButtonValid
              disabled={currentStep === lastStep || isLoading || undefined}
              errors={stepSpecificErrors}
              name={'next_step'}
              onClick={onNext}
              suffix={getIconForAction('next')}
              type={isDraft ? 'primary' : 'secondary'}
            >
              {t('Next')}
            </ButtonValid>
          </>
        }
        right={
          <>
            <SaveCampaignButton
              campaign={campaign}
              childModels={childModels}
              grandchildModels={grandchildModels}
              key={'save'}
              shouldChangeState={false}
              showIcon={true}
              type={
                [CampaignState.Published, CampaignState.Publishing, CampaignState.Stopped].includes(
                  campaign.get('state'),
                )
                  ? 'primary'
                  : 'secondary'
              }
              // We shouldn't allow saving invalid content to a published/stopped campaign
              validate={
                [CampaignState.Published, CampaignState.Publishing, CampaignState.Stopped].includes(
                  campaign.get('state'),
                )
                  ? getValidationErrors
                  : undefined
              }
            />
            {(isDraft || isStopped) && (
              <ButtonValid
                disabled={completedStep < 6 || isPublishing || isPastEndDate || undefined}
                errors={allErrors}
                name={'publish'}
                onClick={handlePublish}
                prefix={getIconForAction('publish')}
                tooltip={isPastEndDate ? disableStopOrRepublishPastEndDate : undefined}
                tooltipPosition={'top-end'}
                type={'success'}
              >
                {t('Publish')}
              </ButtonValid>
            )}
          </>
        }
        usePortal={true}
      />
      <div data-appcues-campaign={campaign.get('_cls')}>
        <WizardV2
          actions={
            showV2Creatives && currentStep === creativesStepIndex ? (
              <AddCreativeButton
                availableChannels={availableChannels}
                campaignId={campaign.id}
                creatives={creatives.models}
                eventId={campaign.get('event')}
                onAppear={poof.animateAppear}
                type={'secondary'}
              />
            ) : undefined
          }
          completed={completedStep}
          current={currentStep}
          isFullWidth={true}
          name={'ad-wizard'}
          onChange={updateCurrentStep}
        >
          <WizardV2.Step {...getStepValidation(0)} title={t('General')}>
            <WizardGeneralStep
              campaign={campaign}
              hasDisplayCreatives={hasDisplayCreatives}
              hasMetaCreatives={hasMetaCreatives}
              onNext={onNext}
            />
          </WizardV2.Step>
          <WizardV2.Step {...getStepValidation(1)} title={t('Audience')}>
            <AdWizardAudienceStep
              campaign={campaign}
              emailValidation={emailValidation}
              targetings={targetings}
            />
          </WizardV2.Step>
          {!hideFilters && (
            <WizardV2.Step {...getStepValidation(2)} title={t('Filters (optional)')}>
              <AdWizardFiltersStep campaign={campaign} targetings={targetings} />
            </WizardV2.Step>
          )}
          <WizardV2.Step {...getStepValidation(hideFilters ? 2 : 3)} title={t('Creatives')}>
            {showV2Creatives ? (
              <AdWizardCreativesStepV2
                campaign={campaign}
                creatives={creatives.models}
                poof={poof}
              />
            ) : (
              <AdWizardCreativesStep campaign={campaign} creatives={creatives.models} />
            )}
          </WizardV2.Step>
          <WizardV2.Step {...getStepValidation(hideFilters ? 3 : 4)} title={t('Optimization')}>
            <AdWizardOptimizationStep
              campaign={campaign}
              onNavigateToTargetsStep={(): void => updateCurrentStep(1)}
              targetings={targetings.models}
            />
          </WizardV2.Step>
          <WizardV2.Step {...getStepValidation(hideFilters ? 4 : 5)} title={t('Budget')}>
            <AdWizardBudgetStep account={account} campaign={campaign} targetings={targetings} />
          </WizardV2.Step>
          <WizardV2.Step {...getStepValidation(hideFilters ? 5 : 6)} title={t('Goals')}>
            <StepGoals campaign={campaign} goals={goals.models} />
          </WizardV2.Step>
          <WizardV2.Step {...getStepValidation(hideFilters ? 6 : 7)} title={t('Review')}>
            <AdWizardReviewStep
              campaign={campaign}
              creatives={creatives.models}
              getValidationErrors={getValidationErrors}
              goals={goals.models}
              targetings={targetings}
            />
          </WizardV2.Step>
        </WizardV2>
      </div>
    </CampaignContext.Provider>
  );
}

export default observer(AdWizard);
